import i18n from "@/lib/i18n";
import { resolveApiUrl } from "@/lib/api";

export const SSO_PENDING_KEY = "ailechat:sso-pending";

async function read(response: Response): Promise<unknown> {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export class SsoApiError extends Error {
  constructor(public readonly status: number, public readonly code: string, message: string) {
    super(message);
    this.name = "SsoApiError";
  }
}

function fail(status: number, body: unknown): never {
  const error = (body as { error?: { code?: string; message?: string } })?.error;
  throw new SsoApiError(status, error?.code ?? "SSO_ERROR", error?.message ?? i18n.t("profilePage.sso.actionFailed"));
}

export type SsoPending = { kind: "exchange" | "link"; code: string; tenant: string | null };

function fromParams(raw: string): SsoPending | null {
  const params = new URLSearchParams(raw.replace(/^[#?]/, ""));
  const exchange = params.get("sso_exchange");
  const link = params.get("sso_link");
  const tenant = params.get("tenant");
  if (exchange) return { kind: "exchange", code: exchange, tenant };
  if (link) return { kind: "link", code: link, tenant };
  return null;
}

export function parseSsoCallback(source: string): SsoPending | null {
  const hashIndex = source.indexOf("#");
  const queryIndex = source.indexOf("?");
  const hash = hashIndex >= 0 ? source.slice(hashIndex + 1) : "";
  const query = queryIndex >= 0 ? source.slice(queryIndex + 1, hashIndex >= 0 ? hashIndex : undefined) : "";
  return fromParams(hash) ?? fromParams(query);
}

export function storeSsoPending(pending: SsoPending): void {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.setItem(SSO_PENDING_KEY, JSON.stringify(pending));
}

export function readSsoPending(): SsoPending | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(SSO_PENDING_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SsoPending;
    if ((parsed.kind === "exchange" || parsed.kind === "link") && typeof parsed.code === "string") return parsed;
  } catch { /* ignore */ }
  return null;
}

export function clearSsoPending(): void {
  if (typeof sessionStorage === "undefined") return;
  sessionStorage.removeItem(SSO_PENDING_KEY);
}

export function stripSsoParamsFromLocation(): void {
  if (typeof window === "undefined" || !window.history?.replaceState) return;
  const url = new URL(window.location.href);
  url.searchParams.delete("sso_exchange");
  url.searchParams.delete("sso_link");
  url.searchParams.delete("tenant");
  url.hash = "";
  window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}`);
}

export async function resolveSsoWorkspace(workspace: string): Promise<{ url: string }> {
  const response = await fetch(resolveApiUrl("/v1/auth/saml/resolve"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ workspace: workspace.trim() }),
  });
  const body = await read(response);
  if (!response.ok) fail(response.status, body);
  return body as { url: string };
}

export async function exchangeSsoLogin(code: string): Promise<unknown> {
  const response = await fetch(resolveApiUrl("/v1/saml-identity/exchange"), {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ code }),
  });
  const body = await read(response);
  if (!response.ok) fail(response.status, body);
  return body;
}

export async function linkSsoIdentity(code: string, token: string): Promise<unknown> {
  const response = await fetch(resolveApiUrl("/v1/saml-identity/link"), {
    method: "POST",
    headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
    body: JSON.stringify({ code }),
  });
  const body = await read(response);
  if (!response.ok) fail(response.status, body);
  return body;
}

export async function listSsoBindings(token: string): Promise<{ bindings: { id: string; tenant_id: string; idp_issuer: string; name_id: string; idp_email: string | null; created_at: number }[] }> {
  const response = await fetch(resolveApiUrl("/v1/saml-identity/bindings"), {
    headers: { authorization: `Bearer ${token}` },
  });
  const body = await read(response);
  if (!response.ok) fail(response.status, body);
  if (!body || typeof body !== 'object' || !Array.isArray((body as {bindings?:unknown}).bindings)) {
    throw new SsoApiError(502,'INVALID_RESPONSE',i18n.t("profilePage.sso.invalidResponse"));
  }
  return body as { bindings: { id: string; tenant_id: string; idp_issuer: string; name_id: string; idp_email: string | null; created_at: number }[] };
}

export async function unlinkSsoBinding(id: string, token: string): Promise<void> {
  const response = await fetch(resolveApiUrl(`/v1/saml-identity/bindings/${encodeURIComponent(id)}`), {
    method: "DELETE",
    headers: { authorization: `Bearer ${token}` },
  });
  if (!response.ok) fail(response.status, await read(response));
}
